import { MutableRefObject } from 'react';
import usePricingSourceStore from 'src/stores/pricing/usePricingSourceStore';
import { useSubscriptionTabRecovery } from 'src/hooks/utils/useSubscriptionTabRecovery';
import {
	PRICING_FALLBACK_COOLDOWN_MS,
	PRICING_MAX_RECOVERY_ATTEMPTS,
	PRICING_RECOVERY_CHECK_INTERVAL_MS,
	PRICING_TAB_INACTIVE_DISCONNECT_DELAY_MS,
} from '../constants';

interface UsePricingTabVisibilityAndRecoveryParams {
	lastFallbackTimestamp: MutableRefObject<number>;
	/**
	 * Callback to perform full recovery. This callback may depend on
	 * destroyCurrentPipeline$ internally, but the tab visibility effect
	 * should NOT directly depend on destroyCurrentPipeline$.
	 */
	performRecovery: () => void;
	/**
	 * Callback for tab inactive cleanup. Called inside the timeout handler.
	 */
	onTabInactiveCleanup: () => void;
}

/**
 * Pricing adapter over the shared `useSubscriptionTabRecovery` module.
 *
 * Maps the pricing transport into the shared tab-recovery interface:
 * - health = pricing source is 'websocket'
 * - teardown/reconnect = the provider's onTabInactiveCleanup/performRecovery
 *   (the pricing transport has no manual source pinning and no eager recovery
 *   gate, so recovery is purely interval- and refocus-driven)
 *
 * FE-4066 semantics (never let the visibility handling depend on the
 * pipeline's destroyCurrentPipeline$ Subject) are enforced structurally by the
 * shared module — see useSubscriptionTabRecovery JSDoc.
 */
export function usePricingTabVisibilityAndRecovery({
	lastFallbackTimestamp,
	performRecovery,
	onTabInactiveCleanup,
}: UsePricingTabVisibilityAndRecoveryParams): void {
	const source = usePricingSourceStore((s) => s.source);

	useSubscriptionTabRecovery({
		// PricingSource is a binary 'websocket' | 'polling' union today, so
		// !isHealthy is exactly the old `source === 'polling'` check. If a
		// third source state is ever added, revisit whether it should count
		// as unhealthy-and-recoverable.
		isHealthy: source === 'websocket',
		lastFallbackTimestamp,
		performRecovery,
		onTabInactiveCleanup,
		timing: {
			fallbackCooldownMs: PRICING_FALLBACK_COOLDOWN_MS,
			recoveryCheckIntervalMs: PRICING_RECOVERY_CHECK_INTERVAL_MS,
			tabInactiveDisconnectDelayMs: PRICING_TAB_INACTIVE_DISCONNECT_DELAY_MS,
			maxRecoveryAttempts: PRICING_MAX_RECOVERY_ATTEMPTS,
		},
	});
}
